export type OtsConfidenceTone = "success" | "caution" | "warning" | "danger" | "neutral";

export type OtsConfidenceRingProps = {
  /** 0–1. Missing means not scored, drawn as an empty ring. */
  value?: number;
  /** Something blocks the subject outright; the ring turns red whatever the score. */
  blocked?: boolean;
  /** Accessible name, e.g. "Business validation confidence 92%". */
  label: string;
  /** Diameter in pixels. */
  size?: number;
  className?: string;
};

/** How good a confidence is: ≥90% good, ≥80% fair, ≥60% poor, below that bad. */
export const getConfidenceTone = (value?: number, blocked = false): OtsConfidenceTone => {
  if (blocked) return "danger";
  if (value == null) return "neutral";
  return value >= 0.9 ? "success" : value >= 0.8 ? "caution" : value >= 0.6 ? "warning" : "danger";
};

const toneColors: Record<OtsConfidenceTone, string> = {
  success: "var(--ots-success)",
  caution: "var(--ots-yellow)",
  warning: "var(--ots-warning)",
  danger: "var(--ots-error)",
  neutral: "var(--ots-outline)",
};

/** A confidence score as a small filled ring, colored by how good it is. */
export const OtsConfidenceRing = ({value, blocked = false, label, size = 16, className}: OtsConfidenceRingProps) => {
  const ratio = value == null ? 0 : Math.min(Math.max(value, 0), 1);
  const stroke = Math.max(2, size / 5.5);
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const tone = getConfidenceTone(value, blocked);
  // A blocked subject with a zero score still needs to read as red, not empty.
  const filled = blocked && ratio === 0 ? 1 : ratio;

  return <svg
    role="img"
    aria-label={label}
    data-tone={tone}
    width={size}
    height={size}
    viewBox={`0 0 ${size} ${size}`}
    className={["shrink-0 -rotate-90", className].filter(Boolean).join(" ")}>
    <circle cx={size / 2} cy={size / 2} r={radius} fill="none" strokeWidth={stroke} stroke="var(--ots-outline-variant)"/>
    {filled > 0 && <circle
      cx={size / 2}
      cy={size / 2}
      r={radius}
      fill="none"
      strokeWidth={stroke}
      strokeLinecap="round"
      stroke={toneColors[tone]}
      strokeDasharray={`${circumference * filled} ${circumference}`}/>}
  </svg>;
};
